'use client';

import { cn } from '@newfront-insurance/core-ui/v2';
import { memo, useMemo } from 'react';
import { Streamdown, type Components } from 'streamdown';

import { remarkPlugins, shikiTheme, streamdownPlugins } from '../../lib/streamdown-config';
import { type TableData } from '../markdown-table/lib';
import { TableWrapper } from '../markdown-table/table-wrapper';

export type MessageResponseProps = {
  children: string;
  className?: string;
  isAnimating?: boolean;
  /** Message UUID for analytics tracking */
  messageUuid?: string;
  /** Callback when user clicks expand on a table (opens in spreadsheet view) */
  onExpandTable?: (tableData: TableData) => void;
  /** Prefix for analytics event names */
  prefix?: string;
};

/**
 * Creates minimal component overrides for MessageResponse.
 * Uses Streamdown's built-in features (code blocks, copy/download) where possible.
 * Only overrides what's necessary for:
 * - Analytics tracking (tables)
 * - Custom callbacks (onExpandTable)
 * - Design system styling
 */
function createMessageComponents(
  messageUuid?: string,
  onExpandTable?: (tableData: TableData) => void,
  prefix?: string,
): Partial<Components> {
  return {
    // Table with analytics + expand callback (Streamdown doesn't support these)
    table: ({ children, ...props }) => (
      <TableWrapper onExpandTable={onExpandTable} prefix={prefix} messageUuid={messageUuid} {...props}>
        {children}
      </TableWrapper>
    ),
    // Citations open a separate document without involving a host router.
    a: ({ children, href, ...props }) => (
      <a className="text-brand-400 hover:underline" target="_blank" rel="noreferrer" href={href ?? '#'} {...props}>
        {children}
      </a>
    ),
  };
}

/**
 * MessageResponse component - the primary markdown renderer for AI responses.
 *
 * Features:
 * - Streaming support with `isAnimating` prop
 * - Syntax highlighting via Shiki (code plugin)
 * - Math equations via KaTeX (math plugin)
 * - Mermaid diagrams (mermaid plugin)
 * - CJK text support (cjk plugin)
 * - Built-in copy/download buttons for code blocks
 * - Custom table handling with analytics + expand callback
 *
 * @example
 * ```tsx
 * <MessageResponse isAnimating={isStreaming} messageUuid={msg.id}>
 *   {markdownContent}
 * </MessageResponse>
 * ```
 */
export const MessageResponse = memo(
  ({ children, className, isAnimating, messageUuid, onExpandTable, prefix }: MessageResponseProps) => {
    const components = useMemo(
      () => createMessageComponents(messageUuid, onExpandTable, prefix),
      [messageUuid, onExpandTable, prefix],
    );

    return (
      <Streamdown
        className={cn('size-full text-[14px] leading-[26px] [&>*:first-child]:mt-0 [&>*:last-child]:mb-0', className)}
        plugins={streamdownPlugins}
        remarkPlugins={remarkPlugins}
        shikiTheme={shikiTheme}
        components={components}
        isAnimating={isAnimating}
        animated={isAnimating}
      >
        {children}
      </Streamdown>
    );
  },
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.isAnimating === nextProps.isAnimating &&
    prevProps.messageUuid === nextProps.messageUuid,
);

MessageResponse.displayName = 'MessageResponse';
